import './listing.scss';

import React, { useState } from 'react';

import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import downloadIcon from '@progymedia/sbi-web-drolet/src/images/icons/download-icon.svg';
import { AddToCartButtonContainer } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart';
import { useAvailabilityInfo } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart/AddToCartButton';
import { useProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';
import { useProductReviews } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useProductReviews';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { Rating } from '@sbi-international/boutik.gatsby.plugin.components';
import droletArrowRight from '../../images/icons/primaryDroletArrowRight.svg';
import GatsbyImageWrapper from '../GatsbyImageWrapper';
import { IAppliance, IFilterOptions } from '../../interfaces';
import { useTranslation } from 'react-i18next';

export default function Listing(props: {
    title?: string;
    items: IAppliance[];
    displayLink: boolean;
    hasOwnPage: boolean;
    displayManual: boolean;
    displayRatings?: boolean;
    filterOptions?: IFilterOptions;
    displayDescription?: boolean;
    displayPreOrder?: boolean;
}) {
    const { t } = useTranslation('layout');
    const { currentLocale, navigateTranslated } = usePageLocalization();

    const {
        title,
        items,
        displayLink,
        hasOwnPage,
        displayManual,
        filterOptions,
        displayRatings,
        displayDescription,
        displayPreOrder,
    } = props;

    return (
        <>
            <div className='drolet-alist-container'>
                {title && <div className='alist-title'>{title}</div>}
                {items &&
                    items?.map((item, index) => (
                        <Single
                            key={item.code}
                            item={item}
                            index={index}
                            displayLink={displayLink}
                            hasOwnPage={hasOwnPage}
                            displayManual={displayManual}
                            displayRatings={displayRatings}
                            filterOptions={filterOptions}
                            displayDescription={displayDescription}
                        />
                    ))}
            </div>
        </>
    );

    function Single(props: {
        item: any;
        index: number;
        displayLink?: boolean;
        hasOwnPage: boolean;
        displayManual?: boolean;
        displayRatings?: boolean;
        filterOptions?: IFilterOptions;
        displayDescription?: boolean;
    }) {
        const [qtyValue, setQtyValue] = useState(1);
        const { item, displayRatings, displayDescription } = props;
        const [price] = useProductPrice(item.code);
        const availability = useAvailabilityInfo(item);

        const productReviews = useProductReviews(item?.code);

        return (
            <div className='alist-single'>
                <div
                    className={
                        hasOwnPage ? 'clickable-zone hasLink' : 'clickable-zone'
                    }
                    onClick={() =>
                        hasOwnPage &&
                        item?.slug?.[currentLocale] &&
                        navigateTranslated(item.slug[currentLocale])
                    }
                >
                    <div className='alist-image'>
                        <GatsbyImageWrapper
                            fields={item?.fields}
                            className='list-img'
                        />
                    </div>
                    <div className='alist-info'>
                        <span className='alist-sku'>{item?.code}</span>
                        <span className='alist-info-title'>
                            {item?.title?.[currentLocale]}
                        </span>

                        {item?.description && displayDescription && (
                            <div
                                className='alist-description'
                                dangerouslySetInnerHTML={{
                                    __html: item?.description
                                        ?.valuesMarkdownRemark?.[currentLocale],
                                }}
                            />
                        )}
                        {displayManual &&
                            item?.documents?.[0][currentLocale] && (
                                <a
                                    rel='noreferrer'
                                    target='_blank'
                                    href={
                                        '/file/' +
                                        item?.documents[0][currentLocale]
                                    }
                                    className='document'
                                >
                                    <span className='alist-download'>
                                        {t('appliance.download-manual')}
                                        <img src={downloadIcon} alt=''></img>
                                    </span>
                                </a>
                            )}

                        {displayRatings && (
                            <div className='alist-reviews'>
                                <Rating.RatingScore
                                    ratingScore={productReviews?.average ?? 0}
                                    ratingCount={productReviews?.count}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className='mobile-cart-group'>
                    <div className='mobile-price-group'>
                        <div className='alist-price'>
                            {LocalePrice(
                                currentLocale,
                                price?.regularPrice.value
                            )}
                        </div>
                        <div className='alist-avail'>
                            {availability.availabilityLabel}
                        </div>
                    </div>
                    <div className='alist-add-to-cart'>
                        {displayPreOrder &&
                            availability.availabilityLabel?.includes(
                                t('addToCartBtn.next-availability')
                            ) &&
                            availability.allowAddToCart && (
                                <span className='preorder-txt'>
                                    {t('addToCartBtn.pre-order')}
                                </span>
                            )}
                        <AddToCartButtonContainer
                            product={item}
                            qtyValue={qtyValue}
                            setQtyValue={setQtyValue}
                            small={true}
                        />
                        {displayLink && (
                            <span
                                className='alist-see-more'
                                onClick={() => {
                                    navigateTranslated(
                                        item?.slug?.[currentLocale]
                                    );
                                }}
                            >
                                {t('appliance.see-more')}
                                <img
                                    className={`alist-iconRight`}
                                    src={droletArrowRight}
                                    alt={''}
                                />
                            </span>
                        )}
                    </div>
                </div>
                {displayLink && (
                    <span
                        className='alist-see-more-small'
                        style={{ display: 'none' }}
                        onClick={() => {
                            navigateTranslated(item?.slug?.[currentLocale]);
                        }}
                    >
                        {t('appliance.see-more')}
                        <img
                            className={`alist-iconRight`}
                            src={droletArrowRight}
                            alt={''}
                        />
                    </span>
                )}
            </div>
        );
    }
}
